import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'process', label: 'Complete Pre-Auth Process' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-csharp', label: 'Sample Complete Pre-Auth Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample Complete Pre-Auth XML' },
  { id: 'data-returned', label: 'Data Returned', children: [
    { id: 'response-notification', label: 'Notification Response' },
    { id: 'response-result', label: 'Transaction Result' },
  ]},
  { id: 'expected-responses', label: 'Expected XML Documents' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=clear&Data=<R>...</R>`;

const CSHARP_SAMPLE = `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
    UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

    string pData = "username=SomeName"; //API Username
    pData += "&password=SomePassword"; //API Password
    pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
    pData += "&ApiSignature=Clear"; //API Signature - Please notice the \`Clear\` signature here for the Complete Pre-Auth request

    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>"); //Data fields required for request

    byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

    httpWReq.Method = "POST"; //HTTP Method - POST
    httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
    httpWReq.ContentLength = data.Length; //Getting Content Length

    //Create POST data and convert it to a byte array.
    byte[] byteArray = Encoding.UTF8.GetBytes(pData);

    // Get the request stream.
    Stream dataStream = httpWReq.GetRequestStream();

    // Write the data to the request stream.
    dataStream.Write(byteArray, 0, byteArray.Length);

    // Close the Stream object.
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse(); //Assign response to \`response\` variable
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd(); //Obtain response from post

    /* Parse XML from response */
    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);
    /* END */

    /* Obtain information from response fields */
    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    //Or loop through the elements
    /* END */

    //More code...

    return View();
}`;

const CPA_XML = `<!-- Complete Pre-Auth Request Demonstration -->
<R>
    <R1>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R1>
    <R2>100</R2>
    <R3>1000596</R3>
</R>`;

const RESP_CONFIRM = `<!-- Sample XML Document If Notification Method Set: Notification, Confirmation Notification will be sent to the Notification URL -->
<?xml version="1.0"?>
<R>
    <R1>0023</R1>
    <R2>Clear received</R2>
</R>`;

const RESP_RESULT = `<!-- Sample XML Document If Its The Transaction Result -->
<?xml version="1.0"?>
<R>
    <R1>0044</R1>
    <R2>Successful</R2>
    <R3>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
    <R4>Successful</R4>
    <R5>Data Not Checked</R5>
    <R8>Match</R8>
    <R9>Ok</R9>
    <R10>Confirm</R10>
    <R11>MerchantData</R11>
    <R12>MerchantReferenceNumber</R12>
    <udf>
        <I>UDF Data</I>
    </udf>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: 'Transaction Code — the Transaction Code used in the Sale request.', req: 'Y', def: 'AN(40)' },
  { name: 'R2', desc: 'Amount To Complete (without decimals). E.g: €100.00 = [10000], €123.67 = [12367], €0.99 = [99].', req: 'Y', def: 'N(20)' },
  { name: 'R3', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'Transaction Code.', regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2', desc: 'Total Transaction Amount (without decimals).', regex: '^[0-9]{0,20}$' },
  { name: 'R3', desc: 'Vendor ID.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
];

const RESP_NOTIF_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
];

const RESP_RESULT_FIELDS = [
  { name: 'R1', desc: (<>Error code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description.', def: 'AN(250)' },
  { name: 'R3', desc: "Merchant's own reference code to the transaction.", def: 'AN(40)' },
  { name: 'R4', desc: 'The transaction status.', def: 'AN(20)' },
  { name: 'R5', desc: 'Response from AVS and CV2 checks (All Match, Security Code Match Only, Address Match Only, No Data Matches, Data Not Checked).', def: 'AN(50)' },
  { name: 'R6', desc: 'Result of the checks on the cardholder address numeric from the AVS/CV2 checks (Match / Not Match).', def: 'AN(20)' },
  { name: 'R7', desc: 'Result of the checks on the cardholder Postcode from the AVS/CV2 checks (Match / Not Match).', def: 'AN(20)' },
  { name: 'R8', desc: 'Result of the checks on the cardholder CV2 code from the AVS/CV2 checks (Match / Not Match).', def: 'AN(20)' },
  { name: 'R9', desc: 'Results of the 3D Secure Checks (OK / Error).', def: 'AN(50)' },
  { name: 'R10', desc: 'Confirmation Type (Confirm / Authorisation).', def: 'AN(10)' },
  { name: 'R11', desc: 'Merchant specified data that will be returned on the Response.', def: 'AN(20)' },
  { name: 'R12', desc: 'Merchant specified transaction identifier that will be returned on the response.', def: 'AN(20)' },
  { name: 'udf', desc: 'UDF List (see Overview).', def: 'AN(50)' },
];

const CompletePreAuth = () => (
  <ContainerPageLayout
    currentId="complete-pre-auth"
    title="Complete Pre-Auth"
    signature="clear"
    subtitle="Releases funds from a previously authorised Pre-Auth transaction — called when the merchant is ready to capture."
    toc={TOC}
    mainTestId="complete-preauth-page"
    sidebarTestId="complete-preauth-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
          The <strong>Complete Pre-Auth</strong> API is only required if the transaction was made using our <Link to="/technical-documentation/api-reference/sale-pre-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">Sale</Link> API while providing <strong>"CA"</strong> into the R1 field of the XML Document for the data parameter.
        </p>
        <p>
          The <strong>"CA"</strong> identifier tells us that the transaction is going to be a Pre-Auth and that the funds will be frozen in a <em>"Pending"</em> state within the consumer's bank account. You must then use our <strong>Complete Pre-Auth</strong> API to process the funds from the consumer's account.
        </p>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Amount guardrail:</strong> The <code className="bg-white px-1 rounded">Amount</code> (R2) value must not exceed the total used in the original Sale request — otherwise the request will fail. Partial captures are supported; you can complete an amount less than or equal to what was pre-authorised.
        </p>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Complete Pre-Auth Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The process of the Complete Pre-Auth API is simple and straightforward. Below demonstrates the steps required to successfully make the request.
      </p>
      <ol className="list-decimal pl-6 space-y-3 text-gray-700 text-base leading-relaxed">
        <li>The transaction information is submitted to our API.</li>
        <li>
          The merchant will receive a response of <strong className="text-[#c32222]">"0023 — Clear Received"</strong> if the notification method is set to <em>Notification</em>; a confirmation notification will be sent to the merchant's Notification URL with the overall result.
          <br />
          The merchant will receive a response of <strong className="text-[#c32222]">"0044 — Successful Transaction"</strong> if the notification method is set to <em>Both</em> or <em>Server</em>. Depending on the notification method setup, the merchant may also receive a confirmation notification. The confirmation notification may contain the response <strong className="text-[#c32222]">"0000 — Successful"</strong> indicating the transaction has now completed the Pre-Auth with the amount specified.
        </li>
      </ol>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        HTTP is used as the request-response protocol between a merchant site and the eCOMM API. In the back end, a merchant submits a HTTP POST request to the eCOMM server; the server will then return an XML document where the merchant must parse the data and act accordingly.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Complete Pre-Auth</code> call must be composed of the following information:</p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = Clear</li>
        <li><strong>Data</strong> = Form data in XML format (<a href="#sample-xml" className="text-[#1F4E79] underline">see sample here</a>)</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-csharp" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample Complete Pre-Auth Request</h3>
          <DownloadButtons />
        </div>
        <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-csharp-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="Clear"
          title="Request Reference — Complete Pre-Auth"
          description="Release funds from an existing Pre-Auth transaction."
          defaultData={CPA_XML.replace('<!-- Complete Pre-Auth Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Complete Pre-Auth</code> request. The <strong>Amount</strong> value must not exceed the total used in the sale request as the request will fail.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">Complete Pre-Auth</code> request, including validation regular expressions.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation (Regex)</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">Complete Pre-Auth</code> XML Document</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-2">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <p className="text-sm text-gray-600 mb-4">
        When forming the <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">data</code> parameter, please refer to the <a href="#available-form-fields" className="text-[#1F4E79] underline hover:text-[#163a5f]">guidelines above</a>.
      </p>
      <CodeBlock code={CPA_XML} lang="xml" testid="cpa-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-3">
        Below are the expected XML fields returned from the request. Depending on which <Link to="/technical-documentation/api-reference/overview#notification-methods" className="text-[#1F4E79] underline hover:text-[#163a5f]">notification method</Link> the merchant has requested, the type of data returned will differ.
      </p>
      <p className="text-gray-700 text-base leading-relaxed">
        The merchant will receive <strong className="text-[#c32222]">0023 — Clear Received</strong> if the notification method is set to <em>Notification</em> (the Confirmation Notification will be sent to the Notification URL). Otherwise, the merchant will receive the transaction result <strong className="text-[#c32222]">0044 — Successful Transaction</strong> directly in the server response.
      </p>

      {/* Notification Response */}
      <div id="response-notification" className="scroll-mt-28 mt-6">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Response — Notification Set: Notification</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {RESP_NOTIF_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Result */}
      <div id="response-result" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Response — Transaction Result</h3>
        <p className="text-sm text-gray-600 mb-3">The merchant will receive the Transaction Result if the request passed validation checks.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#1F4E79] text-white">
                <th className="text-left p-3 w-20">Field</th>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3 w-28">Definition</th>
              </tr>
            </thead>
            <tbody>
              {RESP_RESULT_FIELDS.map((f, i) => (
                <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                  <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                  <td className="p-3">{f.desc}</td>
                  <td className="p-3 font-mono text-xs">{f.def}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Expected Responses */}
    <section id="expected-responses" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Expected XML Documents</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates the expected XML document responses returned from the HTTP POST once the request has passed validation.
      </p>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 mb-6">
        <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li><strong className="text-[#c32222]">0023</strong> — Means the notification method is set to "Notification". The confirmation notification will be sent to the Notification URL.</li>
          <li><strong className="text-[#c32222]">0044</strong> — Means the transaction was successful and the funds are now processed. The merchant may also receive a confirmation notification depending on the notification method setup.</li>
        </ol>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response — Notification Method Set: Notification (0023)</h3>
      <CodeBlock code={RESP_CONFIRM} lang="xml" testid="resp-confirm-code" />

      <h3 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response — Transaction Result (0044)</h3>
      <CodeBlock code={RESP_RESULT} lang="xml" testid="resp-result-code" />
    </section>
  </ContainerPageLayout>
);

export default CompletePreAuth;
